import { useState } from "react";
import axios from "axios";
import { CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClasses =
  "w-full bg-white border border-navy-900/15 rounded-lg px-4 py-3.5 font-body text-navy-900 placeholder:text-navy-900/40 transition-colors duration-200 focus:border-navy-900 focus:outline-none";

const labelClasses =
  "font-body text-xs uppercase tracking-[0.18em] text-navy-700 mb-2 block";

export const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    business_name: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | sending | success | error

  const update = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="bg-warm-neutral py-24 md:py-32"
    >
      <div className="container-page max-w-3xl">
        <Reveal>
          <p className="font-body text-xs sm:text-sm uppercase tracking-[0.25em] text-navy-700 mb-5">
            Prefer to write first?
          </p>
          <h2 className="font-heading font-semibold text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-navy-900">
            Not Ready to <span className="italic font-normal">Schedule?</span>
          </h2>
          <p className="font-body text-base sm:text-lg leading-relaxed text-navy-900/70 mt-5 max-w-xl">
            Send me a message and I&rsquo;ll respond within one business day.
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          {status === "success" ? (
            <div
              data-testid="contact-success"
              className="mt-12 flex items-start gap-4 rounded-lg border border-navy-900/15 bg-white p-8"
            >
              <CheckCircle2
                className="h-6 w-6 shrink-0 text-navy-900 mt-0.5"
                aria-hidden="true"
              />
              <p className="font-body text-lg leading-relaxed text-navy-900">
                Thank you. Your message has been sent. I&rsquo;ll respond within
                one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-12" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="cf-name" className={labelClasses}>
                    Name
                  </label>
                  <input
                    id="cf-name"
                    data-testid="contact-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    className={inputClasses}
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label htmlFor="cf-email" className={labelClasses}>
                    Email address
                  </label>
                  <input
                    id="cf-email"
                    data-testid="contact-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    className={inputClasses}
                    placeholder="you@example.com"
                  />
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="cf-business" className={labelClasses}>
                  Business name
                </label>
                <input
                  id="cf-business"
                  data-testid="contact-business"
                  type="text"
                  required
                  value={form.business_name}
                  onChange={update("business_name")}
                  className={inputClasses}
                  placeholder="Your business name"
                />
              </div>

              <div className="mt-6">
                <label htmlFor="cf-message" className={labelClasses}>
                  What do you need help with?
                </label>
                <textarea
                  id="cf-message"
                  data-testid="contact-message"
                  required
                  rows={5}
                  value={form.message}
                  onChange={update("message")}
                  className={`${inputClasses} resize-y`}
                  placeholder="Tell me a little about your bookkeeping situation."
                />
              </div>

              {status === "error" && (
                <p
                  data-testid="contact-error"
                  className="font-body text-sm text-destructive mt-5"
                >
                  Something went wrong sending your message. Please try again in a
                  moment.
                </p>
              )}

              <div className="mt-8">
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={status === "sending"}
                  className="inline-flex items-center justify-center rounded-full font-body font-semibold tracking-wide px-8 py-4 text-base md:text-lg bg-navy-900 text-warm-neutral transition-colors duration-300 hover:bg-navy-800 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status === "sending" ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
